import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, Timestamp, updateDoc, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Toast from './Toast';
import '../styles/SessionsView.css';

const SessionsView = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionOrders, setSessionOrders] = useState([]);
  const [sessionSales, setSessionSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [toast, setToast] = useState(null);
  const [summary, setSummary] = useState({
    orderCount: 0,
    totalSales: 0,
    totalGST: 0,
    subtotal: 0,
    cashSales: 0,
    transferSales: 0,
    cardSales: 0,
    cashCount: 0,
    transferCount: 0,
    cardCount: 0
  });

  const GST_RATE = 0.08;

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // For GST-inclusive pricing: extract GST from the inclusive amount
  const calculateBasePrice = (inclusiveAmount) => {
    return inclusiveAmount / (1 + GST_RATE);
  };

  const calculateGST = (inclusiveAmount) => {
    return inclusiveAmount - calculateBasePrice(inclusiveAmount);
  };

  // Close/End an active session
  const closeSession = async (session) => {
    if (session.status !== 'active') {
      showToast('This session is already closed', 'warning');
      return;
    }

    const confirmMessage = `Are you sure you want to close this session?\n\nStarted: ${formatDate(session.startTime)} at ${formatTime(session.startTime)}\nOpening Cash: MVR ${session.openingCash.toFixed(2)}\n\nNote: This will close the session without proper cash counting. It's recommended to use "End of Day" from the Cashier View for proper session closure.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await updateDoc(doc(db, 'sessions', session.id), {
        status: 'closed',
        endTime: new Date().toISOString(),
        closingCash: 0,
        expectedCash: 0,
        difference: 0,
        forceClosed: true,
        closedFrom: 'SessionsView'
      });

      showToast('Session closed successfully', 'success');

      // Refresh sessions list
      await fetchSessions();

      // Update selected session if it was the one closed
      if (selectedSession?.id === session.id) {
        setSelectedSession(null);
      }
    } catch (error) {
      console.error('Error closing session:', error);
      showToast('Failed to close session', 'error');
    }
  };

  // Navigate to Cashier View to end session properly
  const endSessionProperly = (session) => {
    if (session.status !== 'active') {
      showToast('This session is already closed', 'warning');
      return;
    }

    // Save session to localStorage for CashierView to pick up
    localStorage.setItem('pendingEndOfDaySession', JSON.stringify(session));

    // Navigate to Cashier View
    navigate('/cashier');

    showToast('Loading session in Cashier View...', 'info');
  };

  // Fetch all sessions
  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const sessionsQuery = query(
        collection(db, 'sessions'),
        orderBy('startTime', 'desc')
      );
      const snapshot = await getDocs(sessionsQuery);
      const sessionsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSessions(sessionsList);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders and sales for a selected session
  const fetchSessionData = async (session) => {
    try {
      setOrdersLoading(true);

      const startTime = new Date(session.startTime);
      const endTime = session.endTime ? new Date(session.endTime) : new Date();

      // Fetch orders confirmed during this session
      const ordersQuery = query(
        collection(db, 'orders'),
        where('status', '==', 'confirmed'),
        where('confirmedAt', '>=', Timestamp.fromDate(startTime)),
        where('confirmedAt', '<=', Timestamp.fromDate(endTime))
      );

      // Fetch sales during this session
      const salesQuery = query(
        collection(db, 'sales'),
        where('timestamp', '>=', Timestamp.fromDate(startTime)),
        where('timestamp', '<=', Timestamp.fromDate(endTime))
      );

      const [ordersSnapshot, salesSnapshot] = await Promise.all([
        getDocs(ordersQuery),
        getDocs(salesQuery)
      ]);

      const orders = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const sales = salesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setSessionOrders(orders);
      setSessionSales(sales);
      calculateSummary(sales);
    } catch (error) {
      console.error('Error fetching session data:', error);
      // If it's an index error, show the full error message (includes link to create index)
      if (error.message && error.message.includes('index')) {
        console.error('❌ Firestore index required!');
        console.error('Full error message:', error.message);
        console.error('Click the link above to create the required index in Firebase Console');
      }
      // Set empty data on error
      setSessionOrders([]);
      setSessionSales([]);
      setSummary({ orderCount: 0, totalSales: 0, totalGST: 0, subtotal: 0 });
    } finally {
      setOrdersLoading(false);
    }
  };

  const calculateSummary = (sales) => {
    const totalSales = sales.reduce((sum, sale) => sum + (sale.totalWithGST || 0), 0);
    const totalGST = sales.reduce((sum, sale) => sum + (sale.gst || 0), 0);
    const subtotal = sales.reduce((sum, sale) => sum + (sale.subtotal || 0), 0);

    // Calculate payment type breakdown
    const cashSales = sales.filter(s => s.paymentType === 'cash').reduce((sum, sale) => sum + (sale.totalWithGST || 0), 0);
    const transferSales = sales.filter(s => s.paymentType === 'transfer').reduce((sum, sale) => sum + (sale.totalWithGST || 0), 0);
    const cardSales = sales.filter(s => s.paymentType === 'card').reduce((sum, sale) => sum + (sale.totalWithGST || 0), 0);

    const cashCount = sales.filter(s => s.paymentType === 'cash').length;
    const transferCount = sales.filter(s => s.paymentType === 'transfer').length;
    const cardCount = sales.filter(s => s.paymentType === 'card').length;

    setSummary({
      orderCount: sales.length,
      totalSales: totalSales,
      totalGST: totalGST,
      subtotal: subtotal,
      cashSales,
      transferSales,
      cardSales,
      cashCount,
      transferCount,
      cardCount
    });
  };

  const handleSessionSelect = (session) => {
    setSelectedSession(session);
    fetchSessionData(session);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (startTime, endTime) => {
    if (!endTime) return 'In Progress';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const hours = Math.floor((end - start) / (1000 * 60 * 60));
    const minutes = Math.floor(((end - start) % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const filteredSessions = sessions.filter(session => {
    if (statusFilter === 'all') return true;
    return session.status === statusFilter;
  });

  const exportToCSV = () => {
    if (!selectedSession) return;

    const csvRows = [];
    csvRows.push('Session Report');
    csvRows.push(`Date: ${formatDate(selectedSession.startTime)}`);
    csvRows.push(`Start Time: ${formatTime(selectedSession.startTime)}`);
    csvRows.push(`End Time: ${formatTime(selectedSession.endTime)}`);
    csvRows.push(`Status: ${selectedSession.status}`);
    csvRows.push('');
    csvRows.push('Cash Summary');
    csvRows.push(`Opening Cash,MVR ${(selectedSession.openingCash || 0).toFixed(2)}`);
    csvRows.push(`Expected Cash,MVR ${(selectedSession.expectedCash || 0).toFixed(2)}`);
    csvRows.push(`Closing Cash,MVR ${(selectedSession.closingCash || 0).toFixed(2)}`);
    csvRows.push(`Difference,MVR ${(selectedSession.difference || 0).toFixed(2)}`);
    csvRows.push('');
    csvRows.push('Sales Summary');
    csvRows.push(`Total Orders,${summary.orderCount}`);
    csvRows.push(`Subtotal,MVR ${summary.subtotal.toFixed(2)}`);
    csvRows.push(`GST (8%),MVR ${summary.totalGST.toFixed(2)}`);
    csvRows.push(`Total Sales,MVR ${summary.totalSales.toFixed(2)}`);
    csvRows.push('');
    csvRows.push('Payment Breakdown');
    csvRows.push(`Cash Sales (${summary.cashCount} orders),MVR ${summary.cashSales.toFixed(2)}`);
    csvRows.push(`Transfer Sales (${summary.transferCount} orders),MVR ${summary.transferSales.toFixed(2)}`);
    csvRows.push(`Card Sales (${summary.cardCount} orders),MVR ${summary.cardSales.toFixed(2)}`);
    csvRows.push('');
    csvRows.push('Orders');
    csvRows.push('Order ID,Time,Payment Type,Items,Subtotal,GST,Total');

    sessionOrders.forEach(order => {
      const itemCount = order.items?.length || 0;
      const time = formatTime(order.confirmedAt?.toDate?.() || order.timestamp);
      const sale = sessionSales.find(s => s.orderId === order.id);
      const paymentType = order.paymentType || sale?.paymentType || 'N/A';
      const totalInclusive = order.totalAmount || 0;
      const subtotal = sale?.subtotal || calculateBasePrice(totalInclusive);
      const gst = sale?.gst || calculateGST(totalInclusive);
      const total = sale?.totalWithGST || totalInclusive;

      csvRows.push(`${order.orderNumber || order.id.substring(0, 8)},${time},${paymentType},${itemCount},${subtotal.toFixed(2)},${gst.toFixed(2)},${total.toFixed(2)}`);
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `session-${selectedSession.id}-${formatDate(selectedSession.startTime)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const printReport = () => {
    if (!selectedSession) return;
    window.print();
  };

  if (loading) {
    return (
      <div className="sessions-view">
        <div className="loading">Loading sessions...</div>
      </div>
    );
  }

  return (
    <div className="sessions-view">
      <div className="sessions-header">
        <div className="header-left">
          <h1>Sessions History</h1>
          <p className="sessions-subtitle">View all cashier sessions and their orders</p>
        </div>
      </div>

      <div className="sessions-container">
        {/* Left Panel - Sessions List */}
        <div className="sessions-list-section">
          <div className="filter-controls">
            <button
              className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-btn ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => setStatusFilter('active')}
            >
              Active
            </button>
            <button
              className={`filter-btn ${statusFilter === 'closed' ? 'active' : ''}`}
              onClick={() => setStatusFilter('closed')}
            >
              Closed
            </button>
          </div>

          <div className="sessions-list">
            {filteredSessions.length === 0 ? (
              <div className="no-sessions">
                <p>No sessions found.</p>
                <p className="help-text">Start a session in Cashier View.</p>
              </div>
            ) : (
              filteredSessions.map(session => (
                <div
                  key={session.id}
                  className={`session-card ${selectedSession?.id === session.id ? 'selected' : ''} ${session.status === 'active' ? 'active-session' : ''}`}
                  onClick={() => handleSessionSelect(session)}
                >
                  <div className="session-card-header">
                    <div className="session-date">{formatDate(session.startTime)}</div>
                    <span className={`status-badge ${session.status}`}>
                      {session.status === 'active' && <span className="status-indicator"></span>}
                      {session.status}
                    </span>
                  </div>
                  <div className="session-time-range">
                    {formatTime(session.startTime)} - {session.endTime ? formatTime(session.endTime) : 'In Progress'}
                  </div>
                  <div className="session-card-info">
                    <div className="info-item">
                      <span className="info-label">Opening Cash:</span>
                      <span className="info-value">MVR {(session.openingCash || 0).toFixed(2)}</span>
                    </div>
                    {session.status === 'closed' && session.difference !== undefined && (
                      <div className="info-item">
                        <span className="info-label">Difference:</span>
                        <span className={`info-value ${session.difference >= 0 ? 'positive' : 'negative'}`}>
                          {session.difference >= 0 ? '+' : ''}MVR {session.difference.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel - Session Details */}
        <div className="session-details-section">
          {!selectedSession ? (
            <div className="no-selection">
              <p>Select a session to view details</p>
            </div>
          ) : ordersLoading ? (
            <div className="loading">Loading session data...</div>
          ) : (
            <div className="session-details">
              {/* Session Info Card */}
              <div className="session-info-card">
                <h2>{formatDate(selectedSession.startTime)}</h2>
                <div className="session-meta">
                  <span className={`status-badge ${selectedSession.status}`}>
                    {selectedSession.status === 'active' && <span className="status-indicator"></span>}
                    {selectedSession.status}
                  </span>
                </div>
                <div className="session-times">
                  <div className="time-item">
                    <span className="time-label">Started:</span>
                    <span className="time-value">{formatTime(selectedSession.startTime)} by {selectedSession.startedBy || 'Cashier'}</span>
                  </div>
                  <div className="time-item">
                    <span className="time-label">Ended:</span>
                    <span className="time-value">
                      {selectedSession.endTime ? formatTime(selectedSession.endTime) : 'In Progress'}
                    </span>
                  </div>
                  <div className="time-item">
                    <span className="time-label">Duration:</span>
                    <span className="time-value">{formatDuration(selectedSession.startTime, selectedSession.endTime)}</span>
                  </div>
                </div>
              </div>

              {/* Cash Summary Grid */}
              {selectedSession.status === 'closed' && (
                <div className="cash-summary-grid">
                  <div className="summary-card">
                    <div className="card-label">Opening Cash</div>
                    <div className="card-value">MVR {(selectedSession.openingCash || 0).toFixed(2)}</div>
                  </div>
                  <div className="summary-card">
                    <div className="card-label">Expected Cash</div>
                    <div className="card-value">MVR {(selectedSession.expectedCash || 0).toFixed(2)}</div>
                  </div>
                  <div className="summary-card">
                    <div className="card-label">Closing Cash</div>
                    <div className="card-value">MVR {(selectedSession.closingCash || 0).toFixed(2)}</div>
                  </div>
                  <div className={`summary-card difference-card ${selectedSession.difference >= 0 ? 'positive' : 'negative'}`}>
                    <div className="card-label">Difference</div>
                    <div className="card-value">
                      {selectedSession.difference >= 0 ? '+' : ''}MVR {(selectedSession.difference || 0).toFixed(2)}
                    </div>
                    <div className="card-subtitle">{selectedSession.difference >= 0 ? 'Over' : 'Short'}</div>
                  </div>
                </div>
              )}

              {/* Sales Summary Grid */}
              <div className="sales-summary-grid">
                <div className="summary-card">
                  <div className="card-label">Total Orders</div>
                  <div className="card-value">{summary.orderCount}</div>
                </div>
                <div className="summary-card">
                  <div className="card-label">Total Sales (incl. GST)</div>
                  <div className="card-value">MVR {summary.totalSales.toFixed(2)}</div>
                </div>
                <div className="summary-card">
                  <div className="card-label">Total GST (8%)</div>
                  <div className="card-value">MVR {summary.totalGST.toFixed(2)}</div>
                </div>
              </div>

              {/* Payment Breakdown */}
              <div className="payment-breakdown-section">
                <h3>Payment Type Breakdown</h3>
                <div className="payment-breakdown-grid">
                  <div className="payment-breakdown-card cash">
                    <div className="payment-icon">💵</div>
                    <div className="payment-info">
                      <div className="payment-label">Cash</div>
                      <div className="payment-amount">MVR {summary.cashSales.toFixed(2)}</div>
                      <div className="payment-count">{summary.cashCount} orders</div>
                    </div>
                  </div>
                  <div className="payment-breakdown-card transfer">
                    <div className="payment-icon">🏦</div>
                    <div className="payment-info">
                      <div className="payment-label">Transfer</div>
                      <div className="payment-amount">MVR {summary.transferSales.toFixed(2)}</div>
                      <div className="payment-count">{summary.transferCount} orders</div>
                    </div>
                  </div>
                  <div className="payment-breakdown-card card">
                    <div className="payment-icon">💳</div>
                    <div className="payment-info">
                      <div className="payment-label">Card</div>
                      <div className="payment-amount">MVR {summary.cardSales.toFixed(2)}</div>
                      <div className="payment-count">{summary.cardCount} orders</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Orders Table */}
              <div className="orders-section">
                <h3>Orders</h3>
                {sessionOrders.length === 0 ? (
                  <div className="no-orders">
                    <p>No orders were confirmed during this session</p>
                  </div>
                ) : (
                  <div className="orders-table-container">
                    <table className="orders-table">
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Time</th>
                          <th>Payment Type</th>
                          <th>Items</th>
                          <th>Subtotal</th>
                          <th>GST</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessionOrders.map(order => {
                          const sale = sessionSales.find(s => s.orderId === order.id);
                          const paymentType = order.paymentType || sale?.paymentType;
                          const totalInclusive = order.totalAmount || 0;
                          const subtotal = sale?.subtotal || calculateBasePrice(totalInclusive);
                          const gst = sale?.gst || calculateGST(totalInclusive);
                          const total = sale?.totalWithGST || totalInclusive;

                          return (
                            <tr key={order.id}>
                              <td className="order-id-cell">#{order.orderNumber || order.id.substring(0, 8)}</td>
                              <td>{formatTime(order.confirmedAt?.toDate?.() || order.timestamp)}</td>
                              <td>
                                <span className={`payment-badge ${paymentType || 'unknown'}`}>
                                  {paymentType === 'cash' && '💵 Cash'}
                                  {paymentType === 'transfer' && '🏦 Transfer'}
                                  {paymentType === 'card' && '💳 Card'}
                                  {!paymentType && 'N/A'}
                                </span>
                              </td>
                              <td>
                                <div className="items-list">
                                  {order.items?.map((item, idx) => (
                                    <div key={idx} className="item-row">
                                      {item.name} x{item.quantity}
                                    </div>
                                  ))}
                                </div>
                              </td>
                              <td>MVR {subtotal.toFixed(2)}</td>
                              <td>MVR {gst.toFixed(2)}</td>
                              <td className="total-cell">MVR {total.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="totals-row">
                          <td colSpan="4"><strong>Totals</strong></td>
                          <td><strong>MVR {summary.subtotal.toFixed(2)}</strong></td>
                          <td><strong>MVR {summary.totalGST.toFixed(2)}</strong></td>
                          <td className="total-cell"><strong>MVR {summary.totalSales.toFixed(2)}</strong></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="session-actions">
                {selectedSession.status === 'active' && (
                  <>
                    <button
                      className="end-properly-btn"
                      onClick={() => endSessionProperly(selectedSession)}
                    >
                      ✅ End This Session Properly
                    </button>
                    <button
                      className="close-session-btn"
                      onClick={() => closeSession(selectedSession)}
                    >
                      🔴 Quick Close (No Cash Count)
                    </button>
                  </>
                )}
                <button className="export-btn" onClick={exportToCSV}>
                  Export CSV
                </button>
                <button className="print-btn" onClick={printReport}>
                  Print Report
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default SessionsView;
